import type { AppointmentContact } from '@ghp/domain'

import type { InspectionDraft } from './data-db'

export type MetPerson = NonNullable<InspectionDraft['metPerson']>

/**
 * Une autre personne que le contact est-elle sur place ? La case « Une autre personne est
 * présente » de l'onglet Infos (#555, recette du 20/10/2026).
 *
 * Sans réponse de l'agent — une fiche ouverte avant la case —, une personne déjà saisie vaut
 * « oui » : sa carte reste ouverte, rien de ce qui y est écrit ne disparaît de l'écran.
 */
export function isOtherPersonPresent(
  draft: Pick<InspectionDraft, 'metPerson' | 'otherPersonPresent'>,
): boolean {
  if (draft.otherPersonPresent !== undefined) return draft.otherPersonPresent

  const person = draft.metPerson

  return person !== null && Object.values(person).some((value) => (value ?? '').trim() !== '')
}

/**
 * La personne rencontrée telle qu'elle part (#555) : le **contact** de la demande quand personne
 * d'autre n'est sur place — celui qu'on appelle est celui qui ouvre et signe —, la carte
 * « Coordonnées de la personne présente » sinon.
 *
 * Sans contact sur la demande, la carte est la seule saisie : elle part telle quelle.
 */
export function metPersonOf(
  draft: Pick<InspectionDraft, 'metPerson' | 'otherPersonPresent' | 'signerCapacity'>,
  contact: AppointmentContact | null,
): MetPerson | null {
  if (contact === null || isOtherPersonPresent(draft)) return draft.metPerson

  return {
    lastName: contact.lastName,
    firstName: contact.firstName,
    phone: contact.phone,
    // La qualité constatée l'emporte sur l'annoncée (M32) ; vidée, elle ne part pas.
    capacity:
      draft.signerCapacity === undefined
        ? contact.capacity
        : draft.signerCapacity.trim() === ''
          ? null
          : draft.signerCapacity.trim(),
  }
}
